import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Collapse,
  Divider,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  TrendingUp,
  TrendingDown,
  AccountBalance,
} from '@mui/icons-material';

interface TransactionsYear {
  tscMonth: number;
  tscYear: number;
  totalIn: number;
  totalOut: number;
  balance: number;
  accumulatedBalance: number;
  projectedAccumulatedBalance: number;
}

interface YearBalanceCardProps {
  transaction: TransactionsYear;
}

const formatCurrency = (value: number): string => {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const getMonthName = (month: number): string => {
  return new Date(0, month - 1).toLocaleString("pt-BR", { month: "long" });
};

export function YearBalanceCard({ transaction }: YearBalanceCardProps) {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const isPositiveBalance = transaction.balance >= 0;
  const isPositiveAccumulated = transaction.accumulatedBalance >= 0;

  return (
    <Card 
      sx={{ 
        mb: 2, 
        borderRadius: 2, 
        boxShadow: 1,
        borderLeft: `4px solid ${isPositiveBalance ? '#4caf50' : '#f44336'}`,
        minHeight: 120,
        width: '100%',
        position: 'relative',
        zIndex: 1,
        backgroundColor: 'white',
        '&:hover': {
          boxShadow: 3,
          transform: 'translateY(-1px)',
        },
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <CardContent sx={{ p: 2, minHeight: 80 }}>
        {/* Header do Card - Sempre Visível */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', mb: 1 }}>
              {getMonthName(transaction.tscMonth)} {transaction.tscYear}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '1.25rem',
                  color: isPositiveBalance ? '#4caf50' : '#f44336'
                }}
              >
                {formatCurrency(transaction.balance)}
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip
                  icon={isPositiveBalance ? <TrendingUp /> : <TrendingDown />}
                  label={isPositiveBalance ? "Positivo" : "Negativo"}
                  size="small"
                  sx={{
                    backgroundColor: isPositiveBalance ? '#e8f5e8' : '#ffebee',
                    color: isPositiveBalance ? '#2e7d32' : '#c62828',
                    fontWeight: 500,
                    fontSize: '0.75rem'
                  }}
                />
                
                <Chip
                  icon={<AccountBalance />}
                  label={`Acum: ${formatCurrency(transaction.accumulatedBalance)}`}
                  size="small"
                  sx={{
                    backgroundColor: isPositiveAccumulated ? '#e8f5e8' : '#ffebee',
                    color: isPositiveAccumulated ? '#2e7d32' : '#c62828',
                    fontWeight: 500,
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
            </Box>
            
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              Saldo do Mês
            </Typography>
          </Box>
          
          <IconButton
            onClick={handleExpandClick}
            size="small"
            sx={{ color: 'text.secondary', p: 0.5, ml: 1 }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        {/* Detalhes Expansíveis */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ my: 2 }} />
          
          <Box>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
              gap: 2, 
              mb: 2 
            }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Entrada
                </Typography>
                <Typography variant="body2" sx={{ color: '#4caf50', fontWeight: 600, fontSize: '1rem' }}>
                  {formatCurrency(transaction.totalIn)}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Saída
                </Typography>
                <Typography variant="body2" sx={{ color: '#f44336', fontWeight: 600, fontSize: '1rem' }}>
                  {formatCurrency(transaction.totalOut)}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Saldo Acumulado
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: isPositiveAccumulated ? '#4caf50' : '#f44336', 
                  fontWeight: 600, 
                  fontSize: '1rem' 
                }}>
                  {formatCurrency(transaction.accumulatedBalance)}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                  Projeção Acumulada
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: transaction.projectedAccumulatedBalance >= 0 ? '#4caf50' : '#f44336', 
                  fontWeight: 600, 
                  fontSize: '1rem' 
                }}>
                  {formatCurrency(transaction.projectedAccumulatedBalance)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}